import { useApolloClient } from "@apollo/client";
import { getDataOrNull, notNull, omitDeep } from "@msys/common";
import {
  DataGrid,
  GridColDef,
  Modal,
  StateStore,
  useLocalStorageAsState,
  useScreenWidth,
  VisibilityIndicatorIcon,
} from "@msys/ui";
import {
  Functions as FunctionsIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { Badge, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { isAutogeneratedRow } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import { capitalize } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import {
  DefineItemPropsOverridesEntryInput,
  namedOperations,
  Props2,
} from "../../../../clients/graphqlTypes.js";
import { FullScreenToggleIconButton } from "../../../commons/button/FullScreenToggleIconButton.js";
import { useAsyncCallbackWithStatus } from "../../../commons/hooks/useCallbackWithStatus.js";
import { renderAllowedValues } from "../../item-properties/helpers.js";
import { useDataGridStateStore } from "../../users/useDataGridStateStore.js";
import { type ItemPropsOverrideFragment } from "../properties.generated.js";
import {
  getPropDisplayedValue,
  getPropertyDisplayedUnit,
  getPropertyType,
  groupProperties,
  hasPropOverride,
  isArrayType,
  isComputedProp,
  isNonComputedProp,
  isNumberType,
  ungroupProperties,
} from "../properties.js";
import { usePropertyHelpers } from "../usePropertyHelpers.js";
import {
  getQuestionControlLabel,
  useQuestionContol,
} from "../useQuestionContol.js";
import { ItemPropsCustomizeOverrideModal } from "./ItemPropsCustomizeOverrideModal.js";
import {
  useDefineItemPropsOverridesMutation,
  useInvalidatePrefixOrgCacheMutation,
  useItemPropsOverrideModalQuery,
} from "./ItemPropsOverrideModal.generated.js";

interface Props {
  templateId: string;
  itemId: string;
  versionNumber: number;
  handleClose(): void;
}

export function ItemPropsOverrideModal({
  templateId,
  itemId,
  versionNumber,
  handleClose,
}: Props) {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const { isMaxPhone } = useScreenWidth();
  const { enqueueSnackbar } = useSnackbar();

  const shouldInvalidateCache = React.useRef<boolean>(false);

  const stateStore = useDataGridStateStore("ItemPropsOverrideModal");

  const [openPropertyKey, setOpenPropertyKey] = React.useState<string | null>(
    null
  );

  const [isFullScreen, setIsFullScreen] = useLocalStorageAsState<boolean>(
    "msys-item-properties-override-modal-fullscreen",
    false,
    true
  );

  const client = useApolloClient();
  const query = useItemPropsOverrideModalQuery({
    client,
    variables: {
      docId: templateId,
      itemId: itemId,
      versionNumber: versionNumber,
      applyOverrides: true,
    },
    fetchPolicy: "no-cache",
  });
  const quoteTemplate = getDataOrNull(
    query.data?.quoteTemplateVersion
  )?.quoteTemplate;
  const item = quoteTemplate?.item?.[0];
  const customizableProps = React.useMemo(
    () =>
      item?.props2
        .filter(
          property => isNonComputedProp(property) && property.customizable
        )
        .map(prop => {
          const itemPropOverride = item.itemPropsOverrides.find(
            po => po.key === prop.key
          );

          return {
            ...prop,
            hasOverride: itemPropOverride
              ? hasPropOverride(itemPropOverride)
              : false,
            override: itemPropOverride,
          };
        }) ?? [],
    [item?.itemPropsOverrides, item?.props2]
  );
  const groupedCustomizableProps = React.useMemo(
    () => groupProperties(customizableProps),
    [customizableProps]
  );

  const [previousPropKey, nextPropKey] = React.useMemo(() => {
    const orderedPropertyKeys = ungroupProperties(groupedCustomizableProps).map(
      prop => prop.key
    );

    const currentKeyIndex = openPropertyKey
      ? orderedPropertyKeys.findIndex(key => key === openPropertyKey)
      : null;

    const previousKey =
      currentKeyIndex !== null
        ? orderedPropertyKeys[currentKeyIndex - 1]
        : null;
    const nextKey =
      currentKeyIndex !== null
        ? orderedPropertyKeys[currentKeyIndex + 1]
        : null;

    return [previousKey, nextKey];
  }, [groupedCustomizableProps, openPropertyKey]);

  const [defineItemPropsOverrides] = useDefineItemPropsOverridesMutation({
    client,
    refetchQueries: [namedOperations.Query.ItemPropsOverrideModal],
  });
  const handleSave = React.useCallback(
    async (newPropertyOverrides: DefineItemPropsOverridesEntryInput | null) => {
      if (!item) {
        throw new Error("Item not found");
      }

      const newOverrides: DefineItemPropsOverridesEntryInput[] = [
        ...item.itemPropsOverrides
          .map(o => {
            if (o.key === openPropertyKey) {
              return null;
            }
            const override = omitDeep(o, "__typename");
            return {
              ...override,
              allowedValuesMedia: override.allowedValuesMedia.map(avm =>
                omitDeep(avm, "id", "__typename")
              ),
            };
          })
          .filter(notNull),
      ];

      if (newPropertyOverrides) {
        newOverrides.push(newPropertyOverrides);
      }

      await defineItemPropsOverrides({
        variables: {
          input: {
            itemId,
            templateId,
            versionNumber,
            overrides: newOverrides,
          },
        },
        awaitRefetchQueries: false,
      });
      enqueueSnackbar(t("Overrides applied", { ns: "QuoteItem" }));

      shouldInvalidateCache.current = true;
    },
    [
      defineItemPropsOverrides,
      enqueueSnackbar,
      item,
      itemId,
      openPropertyKey,
      t,
      templateId,
      versionNumber,
    ]
  );

  const [invalidatePrefixOrgCache] = useInvalidatePrefixOrgCacheMutation({
    client,
  });

  const [handleCloseModal, isProcessing] = useAsyncCallbackWithStatus(
    async () => {
      if (shouldInvalidateCache.current) {
        await invalidatePrefixOrgCache();
        // we need refetch observable queries since applyOverrides are not in normalized cache
        await client.reFetchObservableQueries();
        shouldInvalidateCache.current = false;
      }

      handleClose();
    }
  );

  return (
    <Modal
      title={t("Customise properties", { ns: "QuoteItem" })}
      actionButtons={[
        {
          label: t("Close", {
            ns: "Global",
          }),
          handleClick: handleCloseModal,
          buttonProps: {
            variant: "text",
            loading: isProcessing,
          },
        },
      ]}
      maxWidth="xl"
      dialogProps={
        !isMaxPhone && isFullScreen ? { fullScreen: true } : undefined
      }
      headerActions={
        !isMaxPhone ? (
          <FullScreenToggleIconButton
            isFullScreen={isFullScreen}
            setIsFullScreen={setIsFullScreen}
          />
        ) : undefined
      }
      alwaysVisible
      handleClose={handleCloseModal}
    >
      {item && (
        <>
          <Stack direction={"column"} spacing={2}>
            {groupedCustomizableProps.map(group => {
              return (
                <Stack
                  direction="column"
                  spacing={1}
                  key={`group-${group.key}`}
                >
                  <Typography variant="h3">
                    {group.key || t("Non-grouped", { ns: "QuoteItem" })}
                  </Typography>
                  <PropertiesTable
                    properties={group.properties}
                    setOpenPropertyKey={setOpenPropertyKey}
                    stateStore={stateStore}
                  />
                </Stack>
              );
            })}
          </Stack>
          {openPropertyKey && (
            <ItemPropsCustomizeOverrideModal
              templateId={templateId}
              itemId={itemId}
              versionNumber={versionNumber}
              propertyKey={openPropertyKey}
              override={
                item.itemPropsOverrides.find(
                  po => po.key === openPropertyKey
                ) || null
              }
              handleSave={handleSave}
              handlePrevious={
                previousPropKey
                  ? () => {
                      setOpenPropertyKey(previousPropKey);
                    }
                  : null
              }
              handleNext={
                nextPropKey
                  ? () => {
                      setOpenPropertyKey(nextPropKey);
                    }
                  : null
              }
              handleClose={() => setOpenPropertyKey(null)}
            />
          )}
        </>
      )}
    </Modal>
  );
}

type PropertiesTableItem = Props2 & {
  hasOverride: boolean;
  override: ItemPropsOverrideFragment | undefined;
};
const PropertiesTable = React.memo(
  ({
    properties,
    setOpenPropertyKey,
    stateStore,
  }: {
    properties: PropertiesTableItem[];
    setOpenPropertyKey: (key: string) => void;
    stateStore: StateStore;
  }) => {
    const { t } = useTranslate(["Global", "QuoteItem"]);
    const { getBoolLabel, getNumberLabel, getUnitLabel } = usePropertyHelpers();
    const { askWhomLabels, askWhenLabels } = useQuestionContol();

    const columns = React.useMemo(
      (): GridColDef<PropertiesTableItem>[] => [
        {
          field: "label",
          headerName: t("Label", {
            ns: "Global",
          }),
          sortable: false,
          groupable: false,
          flex: 1,
          renderCell({ row: property }) {
            return (
              <Typography
                variant="body2"
                color={property.hasOverride ? "secondary" : "primary"}
              >
                {property.label}
              </Typography>
            );
          },
        },
        {
          field: "type",
          headerName: t("Type", {
            ns: "QuoteItem",
          }),
          sortable: false,
          groupable: false,
          flex: 1,
          valueGetter(value, property) {
            return getPropertyType(property);
          },
          valueFormatter(value) {
            return capitalize(value);
          },
        },

        {
          field: "unit",
          headerName: t("Unit", {
            ns: "QuoteItem",
          }),
          sortable: false,
          groupable: false,
          flex: 1,
          valueGetter(value, property) {
            return isNumberType(property) && property.unit
              ? property.unit
              : null;
          },
          valueFormatter(value) {
            return value ? getUnitLabel(value) : "";
          },
        },
        {
          field: "allowMultiple",
          headerName: t("Allow multiple", {
            ns: "QuoteItem",
          }),
          sortable: false,
          groupable: false,
          flex: 2,
          valueGetter(value, property) {
            return isArrayType(property);
          },
          valueFormatter(value) {
            return value === true || value === false ? getBoolLabel(value) : "";
          },
        },
        {
          field: "allowedValues",
          headerName: t("Allowed values", {
            ns: "QuoteItem",
          }),
          sortable: false,
          groupable: false,
          flex: 2,
          renderCell({ row: property }) {
            const hasAllowedValuesOverride =
              (property.override?.allowedValuesExclude &&
                property.override?.allowedValuesExclude.length > 0) ||
              (property.override?.allowedValuesMedia &&
                property.override?.allowedValuesMedia.length > 0);

            return (
              <Badge
                color="secondary"
                variant="dot"
                invisible={!hasAllowedValuesOverride}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                sx={{ ".MuiBadge-badge": { top: 4 } }}
              >
                {isNonComputedProp(property)
                  ? renderAllowedValues(
                      property,
                      t,
                      getNumberLabel,
                      getBoolLabel
                    ) || "–"
                  : null}
              </Badge>
            );
          },
        },
        {
          field: "prompt",
          headerName: t("Prompt", {
            ns: "QuoteItem",
          }),
          sortable: false,
          groupable: false,
          flex: 2,
          valueGetter(value, property) {
            return isNonComputedProp(property) ? property.prompt : null;
          },
        },
        {
          field: "questionControl",
          headerName: t("Question control", {
            ns: "QuoteItem",
          }),
          sortable: false,
          groupable: false,
          flex: 2,
          renderCell({ row: property }) {
            const hasQuestionControlOverride =
              property.override?.questionControl !== undefined &&
              property.override?.questionControl !== null;
            return (
              <Badge
                color="secondary"
                variant="dot"
                invisible={!hasQuestionControlOverride}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                sx={{ ".MuiBadge-badge": { top: 4 } }}
              >
                {isNonComputedProp(property)
                  ? getQuestionControlLabel(property.questionControl, {
                      askWhenLabels,
                      askWhomLabels,
                    }) || "–"
                  : null}
              </Badge>
            );
          },
        },
        {
          field: "group",
          headerName: t("Group", { ns: "QuoteItem" }),
          sortable: false,
          groupable: false,
          // hideable: true,
          flex: 1,
          valueGetter(value, property) {
            return property.group;
          },
        },
        {
          field: "askIfExpr",
          headerName: t("Ask if", { ns: "QuoteItem" }),
          sortable: false,
          groupable: false,
          flex: 0,
          renderCell({ row: property }) {
            return isNonComputedProp(property) && property.askIfExpr ? (
              <FunctionsIcon color="secondary" />
            ) : null;
          },
        },
        {
          field: "value",
          headerName: t("Value", { ns: "Global" }),
          sortable: false,
          groupable: false,
          flex: 1,
          renderCell({ row: property, rowNode }) {
            const hasValueOverride =
              property.override?.valueAsJson !== undefined &&
              property.override?.valueAsJson !== null;

            const unit = getPropertyDisplayedUnit(property, getUnitLabel);
            const displayedValue = getPropDisplayedValue(
              property,
              getBoolLabel,
              getNumberLabel,
              "–",
              unit
            );

            return (
              <Badge
                color="secondary"
                variant="dot"
                invisible={!hasValueOverride}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                sx={{ ".MuiBadge-badge": { top: 4 } }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: "center",
                  }}
                >
                  <span>{displayedValue}</span>
                  {isComputedProp(property) && (
                    <FunctionsIcon fontSize="small" color="secondary" />
                  )}
                </Stack>
              </Badge>
            );
          },
        },
        {
          field: "visibility",
          headerName: t("Visibility", { ns: "Global" }),
          sortable: false,
          groupable: false,
          flex: 0,
          // minWidth: 34,
          renderCell({ row: property }) {
            const hasVisibilityOverride =
              property.override?.clientVisibility !== undefined &&
              property.override?.clientVisibility !== null;
            return (
              <Badge
                color="secondary"
                variant="dot"
                invisible={!hasVisibilityOverride}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                sx={{ ".MuiBadge-badge": { top: 4 } }}
              >
                <VisibilityIndicatorIcon
                  isVisible={property.clientVisibility}
                />
              </Badge>
            );
          },
        },
        {
          field: "configure",
          headerName: "",
          sortable: false,
          groupable: false,
          flex: 0,
          minWidth: 34,
          maxWidth: 34,
          renderCell({ row: property }) {
            return (
              <IconButton
                color="primary"
                size={"small"}
                sx={{ marginLeft: "-10px" }}
                onClick={() => {
                  setOpenPropertyKey(property.key);
                }}
              >
                <Badge
                  color="secondary"
                  variant="dot"
                  invisible={!property.hasOverride}
                >
                  <Tooltip title={t("Customise property", { ns: "QuoteItem" })}>
                    <SettingsIcon />
                  </Tooltip>
                </Badge>
              </IconButton>
            );
          },
        },
      ],
      [
        t,
        getUnitLabel,
        getNumberLabel,
        getBoolLabel,
        askWhenLabels,
        askWhomLabels,
        setOpenPropertyKey,
      ]
    );

    return (
      <DataGrid<PropertiesTableItem>
        stateStore={stateStore}
        columns={columns}
        // disableColumnMenu
        disableVirtualization
        rows={properties}
        getRowId={row => row.key}
        paginationMode="client"
        sortingMode="client"
        disableRowSelectionOnClick
        onCellClick={(params, event) => {
          event.defaultMuiPrevented = true;
          if (
            !isAutogeneratedRow(params.row) &&
            [
              "customizable",
              "allowedValues",
              "prompt",
              "questionControl",
              "group",
              "askIfExpr",
              "value",
              "visibility",
            ].includes(params.field)
          ) {
            setOpenPropertyKey(params.row.key);
          }
        }}
      />
    );
  }
);
